import { useState, useEffect, useMemo } from 'react';

/**
 * Helper function to darken a color
 * @param {string} color - Hex color code
 * @param {number} factor - Factor to darken by (0.1 = 10% darker)
 * @returns {string} Darkened hex color
 */
const darkenColor = (color, factor = 0.1) => {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Parse RGB components
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Darken each component
  const darkenedR = Math.round(r * (1 - factor));
  const darkenedG = Math.round(g * (1 - factor));
  const darkenedB = Math.round(b * (1 - factor));
  
  // Convert back to hex
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
};

/**
 * Custom hook for managing fonts based on disability profiles
 * @param {Object} disabilityProfile - The selected disability profile
 * @param {string} fontCategory - Optional font category override
 * @returns {Object} Font configuration and utilities
 */
export const useFontManager = (disabilityProfile, fontCategory = null) => {
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const [fontLoadError, setFontLoadError] = useState(null);
  const [fontConfig, setFontConfig] = useState(null);

  // Load font configuration
  useEffect(() => {
    fetch('/font-config.json')
      .then(response => response.json())
      .then(data => setFontConfig(data))
      .catch(error => console.error('Failed to load font config:', error));
  }, []);

  // Get the recommended font for the current disability
  const recommendedFont = useMemo(() => {
    if (!fontConfig || !disabilityProfile?.id) {
      return fontConfig?.disabilityRecommendations?.default || null;
    }

    const disabilityId = disabilityProfile.id;
    return fontConfig.disabilityRecommendations[disabilityId] || 
           fontConfig.disabilityRecommendations.default;
  }, [disabilityProfile, fontConfig]);

  // Get font details for the recommended font
  const fontDetails = useMemo(() => {
    if (!fontConfig || !recommendedFont) return null;
    
    const primaryFontId = recommendedFont.primary;
    
    // Find the font in any category
    for (const category of Object.values(fontConfig.fontCategories)) {
      if (category.fonts[primaryFontId]) {
        return {
          ...category.fonts[primaryFontId],
          id: primaryFontId,
          category: category.name
        };
      }
    }
    
    return null;
  }, [recommendedFont, fontConfig]);

  // Get CSS font family string
  const fontFamily = useMemo(() => {
    if (!fontDetails) return 'Arial, sans-serif';
    return fontDetails.family;
  }, [fontDetails]);

  // Get CSS variables for the selected font
  const fontCssVariables = useMemo(() => {
    const variables = {};
    
    if (disabilityProfile) {
      // Map disability-specific font settings
      switch (disabilityProfile.id) {
        case 'dyslexia':
          variables['--active-font-family'] = 'var(--font-dyslexia)';
          variables['--active-line-height'] = 'var(--line-height-dyslexia)';
          variables['--active-letter-spacing'] = 'var(--letter-spacing-dyslexia)';
          break;
        case 'low_vision':
        case 'blindness':
          variables['--active-font-family'] = 'var(--font-low-vision)';
          variables['--active-line-height'] = 'var(--line-height-accessibility)';
          variables['--active-letter-spacing'] = 'var(--letter-spacing-low-vision)';
          break;
        case 'cerebral_palsy':
        case 'parkinsons_disease':
        case 'muscular_dystrophy':
          variables['--active-font-family'] = 'var(--font-motor-accessible)';
          variables['--active-line-height'] = 'var(--line-height-accessibility)';
          break;
        case 'autism_spectrum_disorder':
        case 'adhd':
          variables['--active-font-family'] = 'var(--font-cognitive-accessible)';
          variables['--active-line-height'] = 'var(--line-height-accessibility)';
          break;
        default:
          variables['--active-font-family'] = 'var(--font-gm-primary)';
          variables['--active-line-height'] = 'var(--line-height-default)';
      }

      // Map disability-specific color settings
      if (disabilityProfile.colors) {
        variables['--primary-color'] = disabilityProfile.colors.primary || '#004C97';
        variables['--secondary-color'] = disabilityProfile.colors.secondary || '#0066CC';
        variables['--background-color'] = disabilityProfile.colors.background || '#FFFFFF';
        variables['--text-color'] = disabilityProfile.colors.text || '#FFFFFF';
        variables['--button-text-color'] = disabilityProfile.colors.buttonText || '#FFFFFF';
        
        // Additional color variations for different component states
        variables['--border-color'] = disabilityProfile.colors.secondary || '#0066CC';
        variables['--focus-color'] = disabilityProfile.colors.primary || '#004C97';
        variables['--primary-hover-color'] = darkenColor(disabilityProfile.colors.primary || '#004C97', 0.8);
      }
    } else {
      variables['--active-font-family'] = 'var(--font-gm-primary)';
      variables['--active-line-height'] = 'var(--line-height-default)';
      
      // Default color scheme
      variables['--primary-color'] = '#004C97';
      variables['--secondary-color'] = '#0066CC';
      variables['--background-color'] = '#FFFFFF';
      variables['--text-color'] = '#FFFFFF';
      variables['--button-text-color'] = '#FFFFFF';
      variables['--border-color'] = '#0066CC';
      variables['--focus-color'] = '#004C97';
      variables['--primary-hover-color'] = '#003366';
    }
    
    return variables;
  }, [disabilityProfile]);

  // Load web fonts if needed
  useEffect(() => {
    const loadWebFonts = async () => {
      // Early return if fontDetails is not available yet
      if (!fontDetails) {
        return;
      }

      // Handle OpenDyslexic loading specially
      if (disabilityProfile?.id === 'dyslexia' && !loadedFonts.has('opendyslexic')) {
        try {
          // Inject OpenDyslexic CSS dynamically to avoid build errors
          const linkId = 'opendyslexic-font';
          if (!document.getElementById(linkId)) {
            const link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            // Use a working CDN for OpenDyslexic
            link.href = 'https://cdn.jsdelivr.net/npm/@fontsource/open-dyslexic@4.5.0/index.css';
            link.onload = () => setLoadedFonts(prev => new Set([...prev, 'opendyslexic']));
            link.onerror = () => console.warn('OpenDyslexic font failed to load, using fallback');
            document.head.appendChild(link);
          }
        } catch (error) {
          console.warn('Failed to load OpenDyslexic font:', error);
        }
      }

      // Handle other web fonts
      if (fontDetails.source === 'web' && fontDetails.webFont && fontDetails.id !== 'opendyslexic') {
        try {
          if (loadedFonts.has(fontDetails.id)) {
            return;
          }

          // For Google Fonts and other CSS-based fonts, inject a link element
          if (fontDetails.webFont.cdnUrl.includes('googleapis.com') || fontDetails.webFont.cdnUrl.includes('.css')) {
            const linkId = `font-${fontDetails.id}`;
            if (!document.getElementById(linkId)) {
              const link = document.createElement('link');
              link.id = linkId;
              link.rel = 'stylesheet';
              link.href = fontDetails.webFont.cdnUrl;
              link.onload = () => setLoadedFonts(prev => new Set([...prev, fontDetails.id]));
              link.onerror = () => console.warn(`${fontDetails.name} font failed to load, using fallback`);
              document.head.appendChild(link);
            }
          } else {
            // For direct font file URLs, use CSS Font Loading API
            if ('fonts' in document) {
              const fontFace = new FontFace(
                fontDetails.name,
                `url(${fontDetails.webFont.cdnUrl})`
              );
              
              await fontFace.load();
              document.fonts.add(fontFace);
              
              setLoadedFonts(prev => new Set([...prev, fontDetails.id]));
            }
          }
        } catch (error) {
          console.warn(`Failed to load font ${fontDetails.name}:`, error);
          setFontLoadError(error.message);
        }
      }
    };

    loadWebFonts();
  }, [fontDetails, loadedFonts, disabilityProfile]);

  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;
    
    Object.entries(fontCssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Cleanup on unmount
    return () => {
      Object.keys(fontCssVariables).forEach(property => {
        root.style.removeProperty(property);
      });
    };
  }, [fontCssVariables]);

  // Get alternative fonts for user selection
  const getAlternativeFonts = () => {
    if (!fontConfig || !recommendedFont) return [];
    
    return recommendedFont.alternatives.map(fontId => {
      for (const category of Object.values(fontConfig.fontCategories)) {
        if (category.fonts[fontId]) {
          return {
            ...category.fonts[fontId],
            id: fontId,
            category: category.name
          };
        }
      }
      return null;
    }).filter(Boolean);
  };

  // Get font recommendation reason
  const getRecommendationReason = () => {
    if (!recommendedFont) return '';
    return recommendedFont.reason;
  };

  // Utility function to get font class name
  const getFontClassName = () => {
    if (!disabilityProfile?.id) return 'font-gm-brand';
    
    switch (disabilityProfile.id) {
      case 'dyslexia':
        return 'font-dyslexia-friendly';
      case 'low_vision':
      case 'blindness':
        return 'font-low-vision';
      case 'cerebral_palsy':
      case 'parkinsons_disease':
      case 'muscular_dystrophy':
        return 'font-motor-accessible';
      case 'autism_spectrum_disorder':
      case 'adhd':
        return 'font-cognitive-accessible';
      default:
        return 'font-gm-brand';
    }
  };

  return {
    // Current font information
    fontFamily,
    fontDetails,
    recommendedFont,
    
    // Font loading state
    isLoaded: fontDetails ? loadedFonts.has(fontDetails.id) : false,
    loadError: fontLoadError,
    
    // CSS utilities
    fontClassName: getFontClassName(),
    cssVariables: fontCssVariables,
    
    // Alternative options
    alternativeFonts: getAlternativeFonts(),
    recommendationReason: getRecommendationReason(),
    
    // Utility functions
    preloadFont: (fontId) => {
      // Implementation for preloading specific fonts
      if (!fontConfig) return null;
      
      const font = Object.values(fontConfig.fontCategories)
        .flatMap(cat => Object.entries(cat.fonts))
        .find(([id]) => id === fontId);
      
      if (font && font[1].source === 'web') {
        // Trigger font load
        return font[1];
      }
      return null;
    }
  };
};